/** @jsxImportSource @emotion/react */
import { useCallback, useRef, useState } from 'react';

import type { ScanPartApprovalStatus } from '@kaiizen/kai-pro-api';
import type { ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';

import { type ProjectStateLifecycle, useProjectStateLifecycle } from '@/Client/Layout/useProjectStateLifecycle';
import { isTaskScopedSafe } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import {
  getGroupOverviewReviewableTasks,
  getGroupOverviewUnreviewedTasks,
  taskParticipatesInReview,
} from '@/Client/Partials/FocusedScopeEditor/focusedGroupOverviewLib';
import {
  useAuth,
  useCreateReviewNotes,
  useDismissOnOutsideInteraction,
  useFeatureFlags,
  useScanEditingContextSlice,
} from '@/Client/State';

import {
  APPROVE_BUTTON_STYLE,
  APPROVE_CONFIRM_STYLE,
  APPROVE_CONTROL_STYLE,
  APPROVED_CHIP_STYLE,
  KaiNavTaskApprovalCheckIcon,
  NO_CURSOR_STYLE,
  SUBMITTING_STYLE,
} from './KaiNavTaskApprovalStyles';
import { NO_APPROVE_PERMISSION_TITLE } from './KaiNavTaskApprovalWidget';
import { canApproveTurnBasedDraftTask, turnBasedDraftOffersTaskApproval } from './turnBasedTaskApprovalLib';

/* ── bulk approve control ──
   Approving everything still waiting in a set, from the header that names the
   set. Group and scope are the same act over different tasks, so this is one
   component mounted twice rather than a group version and a scope version. */

// Single source of truth so a copy change only needs to happen here.
export const BULK_CONFIRM_APPROVAL_LABEL = 'Confirm Approval';
export const BULK_APPROVED_LABEL = 'Approved';

/**
 * Whether `KaiNavTaskBulkApprovalWidget` would render anything for this set —
 * exported so a host that needs to know in advance (the header's own kebab,
 * which stays visible whenever the approve control sits beside it) doesn't
 * have to re-derive this by hand.
 */
export const tasksOfferBulkApproval = (
  tasks: readonly ScanTaskOptimized[],
  {
    projectStateLifecycle,
    approvalStatusByTaskId,
    scanIsChangeOrder,
    changedScopedTaskIds,
    turnBasedHandoff = false,
    allowTurnBasedDraftApproval = false,
  }: {
    projectStateLifecycle: ProjectStateLifecycle;
    approvalStatusByTaskId?: Record<string, ScanPartApprovalStatus | undefined>;
    scanIsChangeOrder: boolean;
    changedScopedTaskIds: readonly string[];
    turnBasedHandoff?: boolean;
    allowTurnBasedDraftApproval?: boolean;
  },
): boolean => {
  const stageOffersApproval = turnBasedHandoff
    ? turnBasedDraftOffersTaskApproval({ projectStateLifecycle, scanIsChangeOrder, allowTurnBasedDraftApproval })
    : projectStateLifecycle === 'review';
  if (!stageOffersApproval) return false;
  const participatingTasks = tasks.filter(task =>
    (!turnBasedHandoff || isTaskScopedSafe(task) === true)
    && taskParticipatesInReview(task.id, scanIsChangeOrder, changedScopedTaskIds)
  );
  return getGroupOverviewReviewableTasks(participatingTasks, approvalStatusByTaskId).length > 0;
};

/**
 * Approve every task in `tasks` that is still waiting on a verdict.
 *
 * Approving is not publishing: this files an approval note per task, which is
 * what eventually satisfies the page header's publish action — it never
 * changes the scan's lifecycle itself.
 *
 * A designated Project Manager has the established per-task and bulk review
 * controls before final approval, without taking the editing turn. The legacy
 * review flow continues to use the organization approval permission.
 */
export const KaiNavTaskBulkApprovalWidget = ({ tasks, label }: {
  /** Every task in this set; which of them still need a verdict is worked out here. */
  tasks: readonly ScanTaskOptimized[];
  label: string;
}) => {
  const projectStateLifecycle = useProjectStateLifecycle();
  const { approvalStatusByTaskId, scanIsChangeOrder, changedScopedTaskIds, project } = useScanEditingContextSlice([
    'approvalStatusByTaskId',
    'scanIsChangeOrder',
    'changedScopedTaskIds',
    'project',
  ]);
  const { userCan, userId } = useAuth();
  const { featureFlags } = useFeatureFlags();
  const createReviewNotes = useCreateReviewNotes();
  const [armed, setArmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useDismissOnOutsideInteraction(buttonRef, armed, useCallback(() => setArmed(false), []));

  const turnBasedProject = featureFlags.turnBasedHandoff && !!project;
  const canApproveTurnBasedDraft = canApproveTurnBasedDraftTask({
    turnBasedHandoff: turnBasedProject,
    managerIds: project?.managerIds,
    userId,
  });

  if (
    !tasksOfferBulkApproval(tasks, {
      projectStateLifecycle,
      approvalStatusByTaskId,
      scanIsChangeOrder,
      changedScopedTaskIds,
      turnBasedHandoff: turnBasedProject,
      allowTurnBasedDraftApproval: canApproveTurnBasedDraft,
    })
  ) return null;

  const participatingTasks = tasks.filter(task =>
    (!turnBasedProject || isTaskScopedSafe(task) === true)
    && taskParticipatesInReview(task.id, scanIsChangeOrder, changedScopedTaskIds)
  );
  const unreviewedTasks = getGroupOverviewUnreviewedTasks(participatingTasks, approvalStatusByTaskId);
  const approved = unreviewedTasks.length === 0;
  const canApprove = turnBasedProject ? canApproveTurnBasedDraft : userCan('approve', 'Scan');
  const disabled = submitting || approved || !canApprove;

  return (
    approved
      ? (
        <div css={[APPROVE_CONTROL_STYLE, APPROVED_CHIP_STYLE, NO_CURSOR_STYLE]} aria-disabled='true'>
          <KaiNavTaskApprovalCheckIcon />
          {BULK_APPROVED_LABEL}
        </div>
      )
      : (
        <button
          type='button'
          ref={buttonRef}
          css={[APPROVE_CONTROL_STYLE, APPROVE_BUTTON_STYLE, armed && APPROVE_CONFIRM_STYLE, submitting && SUBMITTING_STYLE]}
          title={canApprove ? `${label} — ${unreviewedTasks.length} awaiting approval` : NO_APPROVE_PERMISSION_TITLE}
          disabled={disabled}
          onClick={async event => {
            event.stopPropagation();
            if (!armed) return setArmed(true);
            setArmed(false);
            setSubmitting(true);
            try {
              await createReviewNotes(unreviewedTasks.map(task => task.id), 'approval');
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {armed
            ? <KaiNavTaskApprovalCheckIcon />
            : null}
          {armed ? BULK_CONFIRM_APPROVAL_LABEL : label}
        </button>
      )
  );
};
